import { Box, Chip, Divider, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useDayStatsInputs } from "../../../hooks/useDayStatsInputs";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { useDepartmentNurseTypes } from "../../../hooks/useDepartmentNurseTypes";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { useRevealWage } from "../../../hooks/useRevealWage";
import { ALERT_CORAL, INK, NEUTRAL, STATUS_BADGE } from "../../../theme/tokens";
import type { DayStats, NurseFullRole, StatsCalculationParams } from "../../../utils/models";
import { ONLY_DAY_INDEX, rowsOnDate, weekIndexOfDate } from "../../../utils/panelWeeks";
import { calculateDayStats } from "../../../utils/statsCalculations";
import { formatNumber, getChipColor } from "../../../utils/utilFunctions";
import { PanelDayFallback } from "../../StaffingChanges/PanelDayFallback";
import ColorfulRole from "../ColorfulRole/ColorfulRole";

type Props = {
	date: DateTime;
	shiftType: string;
};

/**
 * Component to display statistics for a specific shift on a given date: the day's stats as the Week view computes them,
 * from the schedule cache with `staffing_schedule_cache` on and the old list off (`useDayStatsInputs`).
 * @param date - The date for which the shift statistics are displayed.
 * @param shiftType The shift whose staff are counted by role.
 */
export const ShiftStats: FC<Props> = ({ date, shiftType }) => {
	const { chosenDepartment } = useChosenDepartment();
	const { isHospital } = useDepartmentFlags();
	const departmentNurseTypes = useDepartmentNurseTypes(chosenDepartment.department_id);
	const schedule = useDayStatsInputs(date);
	const { weeks, nurses, wageData, slotOf } = schedule;
	const timezone = chosenDepartment.timezone;
	const canViewWage = useRevealWage();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const dayStats = useMemo<DayStats | undefined>(() => {
		const week = weeks[weekIndexOfDate(weeks, date)];
		// Each row's own day for the date: pairing the rows by position would mix dates where a row skips a day.
		const dayRows = week === undefined ? [] : rowsOnDate(week.weeklyShifts, date);
		if (dayRows.length === 0 || isHospital) return;

		const params: StatsCalculationParams = {
			weeklyShifts: dayRows,
			nurses,
			dayIndex: ONLY_DAY_INDEX,
			department: chosenDepartment,
			timezone: timezone,
			rolesToDisplay: departmentNurseTypes,
			jobManagementEnabled,
			wageData,
		};

		// Match the weekly view's SNF arg (WeekViewProvider passes !isHospital, not isSnf) so an
		// ALF department applies the same role-filter behavior as the desktop grid.
		return calculateDayStats(params, !isHospital, timezone);
	}, [weeks, nurses, date, chosenDepartment, departmentNurseTypes, timezone, isHospital, jobManagementEnabled, wageData]);

	const chipColor = useMemo(() => {
		if (!dayStats) {
			return { backgroundColor: ALERT_CORAL, color: `${alpha(INK.SECONDARY, 1)}` };
		}

		return getChipColor(dayStats, chosenDepartment);
	}, [dayStats, chosenDepartment]);

	const roleCounters = useMemo(() => {
		const counters: Record<string, number> = {};
		const slot = slotOf(date, shiftType);

		if (slot) {
			const nursesInShift = weeks[slot.weekIndex].weeklyShifts[slot.shiftIndex].days[slot.dayIndex].nurses;

			nursesInShift.forEach((nurse) => {
				counters[nurse.role] = (counters[nurse.role] || 0) + 1;
			});
		}

		return counters;
	}, [weeks, slotOf, date, shiftType]);

	if (schedule.isLoading || schedule.isError) return <PanelDayFallback panelDay={schedule} dataTestId="shift-stats" />;

	return (
		<Box
			data-testid="shift-stats-container"
			sx={{
				width: "100%",
				bgcolor: NEUTRAL[50],
				p: 1,
			}}
		>
			<Box sx={{ mb: 1 }}>
				<Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
					<Chip
						label={`${dayStats ? formatNumber((dayStats.totalHours / dayStats.wantedHours) * 100, 0) : 0}%`}
						sx={{
							backgroundColor: chipColor.backgroundColor,
							color: chipColor.color,
							fontWeight: 600,
							fontSize: "16px",
							borderRadius: "20px",
						}}
					/>
				</Box>

				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						gap: 4,
					}}
				>
					<Box sx={{ display: "flex", alignItems: "baseline" }}>
						<Typography sx={{ color: STATUS_BADGE.BRAND_FALLBACK, fontSize: "14px", mr: 1 }}>$PPD</Typography>
						<Typography sx={{ color: INK.SECONDARY, fontWeight: 600, fontSize: "14px" }}>${!canViewWage ? "0.0" : dayStats?.ppd}</Typography>
					</Box>
					<Box sx={{ display: "flex", alignItems: "baseline" }}>
						<Typography sx={{ color: STATUS_BADGE.BRAND_FALLBACK, fontSize: "14px", mr: 1 }}>HPPD</Typography>
						<Typography sx={{ color: INK.SECONDARY, fontWeight: 600, fontSize: "14px" }}>{dayStats?.hppd}</Typography>
					</Box>
				</Box>
			</Box>
			<Divider sx={{ mb: 2, marginInline: 2 }} />
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					gap: 2,
					maxWidth: "100%",
					flexWrap: "wrap",
				}}
			>
				{roleCounters &&
					Object.keys(roleCounters).map((roleKey: string) => (
						<Box key={roleKey} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<ColorfulRole role={roleKey as NurseFullRole} />
							<Typography sx={{ color: INK.SECONDARY, fontWeight: 500, fontSize: "14px" }}>{roleCounters[roleKey as NurseFullRole]}</Typography>
						</Box>
					))}
			</Box>
		</Box>
	);
};
